import React from "react";
import { Link } from "react-router-dom";
import { SocialIcon } from "react-social-icons/component";
import "react-social-icons/x";
import "react-social-icons/telegram";
import "react-social-icons/medium";
import "react-social-icons/youtube";
import "react-social-icons/github";
import { SolstackMark } from "../common/Logo";
import { DappStoreBadge } from "../common/DappStoreBadge";
import { appUrl } from "../../lib/appBase";
import { TOOL_PATHS } from "../../lib/slugs";

const SOCIALS = [
  { network: "x", label: "X", url: "https://x.com/solstack_xyz" },
  { network: "telegram", label: "Telegram", url: "https://t.me/solstack_xyz" },
  { network: "medium", label: "Medium", url: "https://medium.com/@solstackxyz" },
  { network: "youtube", label: "YouTube", url: "https://www.youtube.com/@Solstack" },
  // The icon's own #24292e circle all but vanishes on the dark theme; black, like X and Medium.
  { network: "github", label: "GitHub", url: "https://github.com/solstackxyz", bgColor: "#000" },
];

/* Every href below resolves to a real route in this app (or, for
   "Explorer", the actual Solana Explorer) — no placeholder destinations.
   Links with nothing real behind them yet (a changelog, an issue tracker)
   are left out rather than pointed at a generic homepage.
   `app: true` entries live in the tool suite — cross-domain to
   app.<domain> when that subdomain is real (see src/lib/appBase.js).
   The Tools column links each tool's landing page (/tools/<slug>), named
   the way people search for it: on every page, it's the strongest
   internal link those pages get. */
const landing = (id) => `/tools/${TOOL_PATHS[id]}`;
const FOOTER_COLS = [
  {
    title: "Tools",
    links: [
      { label: "Solana Token Creator", href: landing("create-spl") },
      { label: "Token-2022 Creator", href: landing("create-spl22") },
      { label: "Solana Multisender", href: landing("multisender") },
      { label: "Token Airdrop", href: landing("airdrop") },
      { label: "Burn SPL Tokens", href: landing("burn") },
      { label: "Reclaim SOL Rent", href: landing("rent-reclaim") },
      { label: "Holder Snapshot", href: landing("snapshot") },
      { label: "All tools", href: "/tools" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Security", href: "/docs/security" },
      { label: "Academy", href: "/academy" },
      { label: "Docs", href: "/docs/overview" },
      { label: "Blog", href: "/blog" },
      { label: "Authority guide", href: "/docs/solana-token-authorities" },
      { label: "Token-2022 extensions", href: "/docs/solana-token-2022-extensions" },
      { label: "Fee breakdown", href: "/docs/overview" },
      { label: "About", href: "/about" },
      { label: "Support", href: "mailto:support@solstack.xyz", external: true },
    ],
  },
  {
    title: "Network",
    links: [
      { label: "Mainnet-beta status", href: "status", app: true },
      { label: "Devnet status", href: "status", app: true },
      { label: "RPC settings", href: "/docs/solana-devnet-vs-mainnet" },
      { label: "Explorer", href: "https://explorer.solana.com", external: true },
    ],
  },
];

export const SolstackFooter = () => {
  return (
    <footer className="hp-footer">
      <div className="hp-footer-top">
        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "var(--font-display)",
            }}
          >
            <SolstackMark size={34} />
            <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1, color: "var(--ink)" }}>
              Solstack
            </span>
          </span>
          <p className="hp-footer-brand-tagline">
            A Solana token toolkit. Your keys never leave your wallet. Most
            tools run entirely in your browser; Multisender and Airdrop finish
            sending on our server, using a key that can only move what that one
            job needs.{" "}
            <Link to="/docs/security" className="footer-link">
              How Solstack works ↗
            </Link>
          </p>
          <span className="nb-sm hp-footer-status">
            <span className="hp-footer-status-dot" aria-hidden="true" />
            <span className="kick" style={{ color: "var(--ink)" }}>
              All systems normal
            </span>
          </span>

          <div className="hp-footer-socials">
            {SOCIALS.map((s) => (
              <SocialIcon
                key={s.network}
                network={s.network}
                url={s.url}
                label={s.label}
                bgColor={s.bgColor}
                style={{ width: 30, height: 30 }}
                target="_blank"
                rel="noreferrer"
              />
            ))}
          </div>
          <DappStoreBadge className="hp-footer-dappstore" height={40} />
        </div>

        {FOOTER_COLS.map((col) => (
          <div key={col.title}>
            <h3 className="kick hp-footer-col-title">{col.title}</h3>
            <div className="hp-footer-links">
              {col.links.map((l) =>
                l.external ? (
                  <a
                    key={l.label}
                    className="footer-link"
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {l.label}
                  </a>
                ) : l.app ? (
                  <a key={l.label} className="footer-link" href={appUrl(l.href)} rel="nofollow">
                    {l.label}
                  </a>
                ) : (
                  <Link key={l.label} className="footer-link" to={l.href}>
                    {l.label}
                  </Link>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="hp-footer-bottom">
        <div className="hp-footer-bottom-inner">
          <span>
            © {new Date().getFullYear()} Solstack. Your keys never leave your
            wallet.
          </span>
          <div className="hp-footer-bottom-links">
            <Link to="/docs/terms">Terms</Link>
            <Link to="/docs/privacy">Privacy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
